import { createMachine } from 'xstate';
import { PatientFormsContext, PatientFormsEvent } from './patient-forms.types';

// eslint-disable-next-line @typescript-eslint/no-unsafe-call
export const patientFormsMachine = createMachine<PatientFormsContext, PatientFormsEvent>({
	id: 'patientForms',
	type: 'parallel',
	predictableActionArguments: true,
	context: {
		iTeroPatient: null,
		invisalignPatient: null,
		invisalignToggle: false,
		defaultInvisalignToggle: false,
		cardMatchingPatients: [],
		matchingPatients: [],
		canCreateNew: true,
		searchResults: [],
		pendingIteroPatient: null,
		pendingInvisalignPatient: null,
		originalIteroPatient: null,
		duplicateError: null
	},
	states: {
		patientCard: {
			type: 'parallel',
			states: {
				iTeroPatient: {
					initial: 'creating',
					states: {
						creating: {
							invoke: {
								src: 'searchForMatchingPatients'
							},
							on: {
								'CARD.FORM_UPDATED': { target: 'creating', actions: ['updateIteroPatient'] },
								'CARD.INVISALIGN_TOGGLED': { target: 'creating', actions: ['clearCardMatchingPatients'] },
								'CARD.CLEAR_CLICKED': { target: 'creating', actions: ['clearAll'] },
								'CARD.MATCHES_FOUND': { actions: ['assignCardMatchingPatients'], cond: 'isCardEditable' },
								'CARD.NO_MATCHES_FOUND': { actions: ['clearCardMatchingPatients'], cond: 'isCardEditable' },
								'SEARCH.PATIENT_SELECTED': [
									{ target: 'selected', cond: 'isAlreadyLinkedPatient', actions: ['commitLinkedFromEvent'] }
								],
								'EXISTING.PATIENT_LOADED': { target: 'selected', actions: ['loadExistingPatient'] },
								'EDITOR.NO_DUPLICATE': { target: 'selected', cond: 'isCheckingDuplicateFromLink' },
								'EDITOR.SAVED_WITHOUT_INVISALIGN': {
									target: 'selected',
									actions: ['updatePendingIteroFromEditor', 'commitPendingToItero']
								},
								'PATIENT_MATCH.NO_MATCHES': [
									{ target: 'selected', cond: 'isPendingIteroOrLinked', actions: ['commitPendingToItero'] }
								],
								'PATIENT_MATCH.ERROR': {
									target: 'selected',
									cond: 'isPendingIteroOrLinked',
									actions: ['commitIteroOnError']
								},
								'LINKING.LINKED': [
									{ target: 'selected', cond: 'isIteroSourceLink', actions: ['commitLinkedPatients'] },
									{ target: 'selected', cond: 'isInvisalignSourceLink', actions: ['commitLinkedPatients'] }
								],
								'LINKING.CREATE_NEW': [
									{ target: 'selected', cond: 'isPendingIteroOrLinked', actions: ['commitPendingToItero'] }
								],
								'LINKING.SKIP_INVISALIGN': {
									target: 'selected',
									cond: 'isPendingIteroOrLinked',
									actions: ['commitPendingToItero']
								}
							}
						},
						selected: {
							invoke: {
								src: 'searchForMatchingPatients'
							},
							on: {
								'CARD.INVISALIGN_TOGGLED': { target: 'selected', actions: ['clearCardMatchingPatients'] },
								'CARD.MATCHES_FOUND': { actions: ['assignCardMatchingPatients'], cond: 'isInvisalignToggleOn' },
								'CARD.NO_MATCHES_FOUND': { actions: ['clearCardMatchingPatients'] },
								'CARD.CLEAR_CLICKED': { target: 'creating', actions: ['clearAll'] },
								'SEARCH.PATIENT_SELECTED': [
									{ target: 'selected', cond: 'isAlreadyLinkedPatient', actions: ['commitLinkedFromEvent'] }
								],
								'PATIENT_MATCH.NO_MATCHES': [
									{ target: 'selected', cond: 'isPendingIteroOrLinked', actions: ['commitPendingToItero'] },
									{ target: 'creating', cond: 'isPendingInvisalignOnly' }
								],
								'PATIENT_MATCH.ERROR': {
									target: 'selected',
									cond: 'isPendingIteroOrLinked',
									actions: ['commitIteroOnError']
								},
								'LINKING.LINKED': [
									{ target: 'selected', cond: 'isIteroSourceLink', actions: ['commitLinkedPatients'] },
									{ target: 'selected', cond: 'isInvisalignSourceLink', actions: ['commitLinkedPatients'] }
								],
								'LINKING.CREATE_NEW': [
									{ target: 'selected', cond: 'isPendingIteroOrLinked', actions: ['commitPendingToItero'] },
									{ target: 'creating', cond: 'isPendingInvisalignOnly' }
								],
								'LINKING.SKIP_INVISALIGN': {
									target: 'selected',
									cond: 'isPendingIteroOrLinked',
									actions: ['commitPendingToItero']
								}
							}
						}
					}
				},
				invisalignPatient: {
					initial: 'none',
					states: {
						none: {
							on: {
								'CARD.SET_DEFAULT_TOGGLE': [
									{
										target: 'creating',
										cond: 'isDefaultToggleOn',
										actions: ['setDefaultInvisalignToggle', 'setInvisalignToggleOn']
									},
									{ actions: ['setDefaultInvisalignToggle'] }
								],
								'CARD.INVISALIGN_TOGGLED': { target: 'creating', actions: ['setInvisalignToggleOn'] },
								'EXISTING.PATIENT_LOADED': { target: 'selected', cond: 'isLoadedPatientLinked' },
								'SEARCH.PATIENT_SELECTED': { target: 'selected', cond: 'isAlreadyLinkedPatient' },
								'LINKING.LINKED': [
									{ target: 'selected', cond: 'isIteroSourceLink' },
									{ target: 'selected', cond: 'isInvisalignSourceLink' }
								],
								'LINKING.CREATE_NEW': {
									cond: 'isPendingInvisalignOnly',
									actions: ['copyPendingInvisalignToItero']
								},
								'EDITOR.NO_DUPLICATE': { target: 'selected', cond: 'isCheckingDuplicateFromLink' },
								'PATIENT_MATCH.NO_MATCHES': [
									{
										target: 'selected',
										cond: 'isPendingInvisalignOnly',
										actions: ['commitPendingToInvisalign', 'copyCommittedInvisalignToItero', 'commitPendingToItero']
									}
								]
							}
						},
						creating: {
							on: {
								'CARD.SET_DEFAULT_TOGGLE': [
									{
										target: 'none',
										cond: 'isDefaultToggleOff',
										actions: ['setDefaultInvisalignToggle', 'clearInvisalignData']
									},
									{ actions: ['setDefaultInvisalignToggle'] }
								],
								'CARD.INVISALIGN_TOGGLED': { target: 'none', actions: ['clearInvisalignData'] },
								'EDITOR.CANCELLED': { target: 'none', cond: 'isIteroSelected', actions: ['clearInvisalignData'] },
								'EDITOR.NO_DUPLICATE': { target: 'selected', cond: 'isCheckingDuplicateFromLink' },
								'EXISTING.PATIENT_LOADED': [{ target: 'selected', cond: 'isLoadedPatientLinked' }, { target: 'none' }],
								'CARD.FORM_UPDATED': { actions: ['clearInvisalignContext'] },
								'CARD.NO_MATCHES_FOUND': { actions: ['copyIteroToInvisalign'], cond: 'canCommitFromCard' },
								'SEARCH.PATIENT_SELECTED': { target: 'selected', cond: 'isAlreadyLinkedPatient' },
								'LINKING.LINKED': [
									{ target: 'selected', cond: 'isIteroSourceLink' },
									{ target: 'selected', cond: 'isInvisalignSourceLink' }
								],
								'LINKING.CREATE_NEW': [
									{
										cond: 'isPendingInvisalignOnly',
										actions: ['copyPendingInvisalignToItero']
									},
									{ target: 'creating', cond: 'isPendingIteroOrLinked', actions: ['copyCommittedIteroToInvisalign'] }
								],
								'PATIENT_MATCH.NO_MATCHES': [
									{
										target: 'selected',
										cond: 'isPendingInvisalignOnly',
										actions: ['commitPendingToInvisalign', 'copyCommittedInvisalignToItero', 'commitPendingToItero']
									},
									{ target: 'creating', cond: 'isPendingIteroOrLinked', actions: ['copyCommittedIteroToInvisalign'] }
								],
								'PATIENT_MATCH.ERROR': { target: 'none', actions: ['clearInvisalignData'] },
								'EDITOR.SAVED_WITHOUT_INVISALIGN': { target: 'none', actions: ['clearInvisalignData'] },
								'LINKING.SKIP_INVISALIGN': { target: 'none', actions: ['clearInvisalignData'] }
							}
						},
						selected: {
							on: {
								'CARD.CLEAR_CLICKED': [
									{ target: 'creating', cond: 'wasInvisalignToggleOn', actions: ['setInvisalignToggleOn'] },
									{ target: 'none', actions: ['clearInvisalignData'] }
								],
								'SEARCH.PATIENT_SELECTED': { target: 'selected', cond: 'isAlreadyLinkedPatient' },
								'LINKING.LINKED': [
									{ target: 'selected', cond: 'isIteroSourceLink' },
									{ target: 'selected', cond: 'isInvisalignSourceLink' }
								],
								'PATIENT_MATCH.NO_MATCHES': [
									{
										target: 'selected',
										cond: 'isPendingInvisalignOnly',
										actions: ['commitPendingToInvisalign', 'copyCommittedInvisalignToItero', 'commitPendingToItero']
									}
								]
							}
						}
					}
				},
				connectivity: {
					initial: 'online',
					states: {
						online: {
							on: {
								'SYSTEM.WENT_OFFLINE': 'offline',
								'PATIENT_MATCH.ERROR': 'offline',
								'SEARCH.ERROR_OFFLINE': 'offline'
							}
						},
						offline: {
							on: { 'SYSTEM.WENT_ONLINE': 'online' }
						}
					}
				},
				readOnly: {
					initial: 'no',
					states: {
						no: {
							on: { 'CARD.SET_READ_ONLY': 'yes' }
						},
						yes: {
							on: { 'CARD.SET_EDITABLE': 'no' }
						}
					}
				},
				committed: {
					initial: 'no',
					states: {
						no: {
							on: {
								'CARD.NO_MATCHES_FOUND': { target: 'yes', cond: 'canCommitFromCard' },
								'EXISTING.PATIENT_LOADED': 'yes',
								'PATIENT_MATCH.NO_MATCHES': 'yes',
								'LINKING.LINKED': 'yes',
								'LINKING.CREATE_NEW': { target: 'yes', cond: 'isPendingIteroOrLinked' },
								'LINKING.SKIP_INVISALIGN': 'yes',
								'PATIENT_MATCH.ERROR': 'yes',
								'EDITOR.NO_DUPLICATE': { target: 'yes', cond: 'isCheckingDuplicateFromLink' },
								'EDITOR.SAVED_WITHOUT_INVISALIGN': 'yes',
								'EDITOR.CANCELLED': { target: 'yes', cond: 'isIteroSelected' },
								'SEARCH.PATIENT_SELECTED': { target: 'yes', cond: 'isAlreadyLinkedPatient' }
							}
						},
						yes: {
							on: {
								'CARD.FORM_UPDATED': 'no',
								'CARD.CLEAR_CLICKED': 'no',
								'CARD.INVISALIGN_TOGGLED': 'no',
								'CARD.MATCHES_FOUND': { target: 'no', cond: 'isCardEditable' },
								'SEARCH.PATIENT_SELECTED': { target: 'no', cond: 'isNotAlreadyLinkedPatient' }
							}
						}
					}
				}
			}
		},
		patientSearch: {
			initial: 'closed',
			states: {
				closed: {
					on: {
						'CARD.SEARCH_CLICKED': 'browsingRecent',
						'CARD.REVIEW_MATCHES_CLICKED': 'searchingMatches',
						'LINKING.CANCELLED': { target: 'browsingRecent', actions: ['clearPending'] },
						'EDITOR.CANCELLED': { target: 'browsingRecent', cond: 'isFillingInvisalignFields', actions: ['clearPending'] }
					}
				},
				browsingRecent: {
					on: {
						'SEARCH.QUERY_ENTERED': 'searchingDB',
						'SEARCH.PATIENT_SELECTED': {
							target: 'closed',
							actions: ['assignPendingFromEvent']
						},
						'SEARCH.CANCELLED': { target: 'closed', actions: ['clearPending'] }
					}
				},
				searchingDB: {
					invoke: {
						src: 'searchPatientsInDB'
					},
					on: {
						'SEARCH.QUERY_ENTERED': { target: 'searchingDB', internal: false },
						'SEARCH.QUERY_CLEARED': 'browsingRecent',
						'SEARCH.RESULTS': { actions: ['assignSearchResults'] },
						'SEARCH.PATIENT_SELECTED': {
							target: 'closed',
							actions: ['assignPendingFromEvent', 'clearSearchResults']
						},
						'SEARCH.CANCELLED': { target: 'closed', actions: ['clearPending', 'clearSearchResults'] },
						'SEARCH.ERROR_OFFLINE': { target: 'closed', actions: ['clearPending', 'clearSearchResults'] }
					}
				},
				searchingMatches: {
					on: {
						'SEARCH.PATIENT_SELECTED': {
							target: 'closed',
							actions: ['assignPendingFromEvent']
						},
						'SEARCH.CANCELLED': { target: 'closed', actions: ['clearPending'] }
					}
				}
			}
		},
		patientMatch: {
			initial: 'idle',
			states: {
				idle: {
					on: {
						'SEARCH.PATIENT_SELECTED': {
							target: 'loading',
							cond: 'shouldRunPatientMatch'
						},
						'EDITOR.NO_DUPLICATE': { target: 'loading', cond: 'shouldRunMatchAfterDuplicateCheck' }
					}
				},
				loading: {
					invoke: {
						src: 'checkCrossSystemMatches'
					},
					on: {
						'PATIENT_MATCH.MATCHES_FOUND': { target: 'idle', actions: ['assignMatchResults'] },
						'PATIENT_MATCH.NO_MATCHES': 'idle',
						'PATIENT_MATCH.ERROR': 'idle'
					}
				}
			}
		},
		patientLinking: {
			initial: 'closed',
			states: {
				closed: {
					on: {
						'PATIENT_MATCH.MATCHES_FOUND': 'active'
					}
				},
				active: {
					on: {
						'LINKING.LINKED': 'closed',
						'LINKING.CANCELLED': 'closed',
						'LINKING.CREATE_NEW': 'closed',
						'LINKING.SKIP_INVISALIGN': 'closed'
					}
				}
			}
		},
		patientEditor: {
			initial: 'closed',
			states: {
				closed: {
					on: {
						'CARD.EDITOR_CLICKED': [
							{ target: 'fillingInvisalignFields', cond: 'isInvisalignToggleOn' },
							{ target: 'editingItero' }
						],
						'CARD.INVISALIGN_TOGGLED': {
							target: 'fillingInvisalignFields',
							cond: 'committedPatientNeedsInvisalignFields',
							actions: ['copyIteroToPending']
						},
						'SEARCH.PATIENT_SELECTED': {
							target: 'fillingInvisalignFields',
							cond: 'needsInvisalignFieldsFilling'
						},
						'LINKING.CREATE_NEW': [
							{ target: 'checkingDuplicateFromLink', cond: 'isPendingInvisalignOnly' },
							{ target: 'fillingInvisalignFields', cond: 'pendingNeedsInvisalignFields' }
						]
					}
				},
				editingItero: {
					on: {
						'EDITOR.SAVED': {
							target: 'checkingDuplicateFromEdit',
							actions: ['updatePendingIteroFromEditor', 'clearDuplicateError']
						},
						'EDITOR.CANCELLED': { target: 'closed', actions: ['clearDuplicateError', 'clearPendingKeepOriginal'] }
					}
				},
				fillingInvisalignFields: {
					on: {
						'EDITOR.SAVED': {
							target: 'checkingDuplicateFromFill',
							actions: ['updatePendingIteroFromEditor', 'clearDuplicateError']
						},
						'EDITOR.SAVED_WITHOUT_INVISALIGN': { target: 'closed', actions: ['updatePendingIteroFromEditor'] },
						'EDITOR.CANCELLED': 'closed'
					}
				},
				checkingDuplicateFromEdit: {
					invoke: {
						src: 'checkDuplicateIteroPatient'
					},
					on: {
						'EDITOR.NO_DUPLICATE': { target: 'closed', actions: ['commitPendingToItero'] },
						'EDITOR.DUPLICATE_FOUND': { target: 'editingItero', actions: ['assignDuplicateError'] },
						'EDITOR.CANCELLED': { target: 'closed', actions: ['clearPendingKeepOriginal'] }
					}
				},
				checkingDuplicateFromFill: {
					invoke: {
						src: 'checkDuplicateIteroPatient'
					},
					on: {
						'EDITOR.NO_DUPLICATE': 'closed',
						'EDITOR.DUPLICATE_FOUND': { target: 'fillingInvisalignFields', actions: ['assignDuplicateError'] },
						'EDITOR.CANCELLED': 'closed'
					}
				},
				checkingDuplicateFromLink: {
					invoke: {
						src: 'checkDuplicateIteroPatient'
					},
					on: {
						'EDITOR.NO_DUPLICATE': { target: 'closed', actions: ['commitBothPending'] },
						'EDITOR.DUPLICATE_FOUND': { target: 'editingIteroFromLink', actions: ['assignDuplicateError'] },
						'EDITOR.CANCELLED': { target: 'closed', actions: ['clearPending'] }
					}
				},
				editingIteroFromLink: {
					on: {
						'EDITOR.SAVED': {
							target: 'checkingDuplicateFromLink',
							actions: ['updatePendingIteroFromEditor', 'clearDuplicateError']
						},
						'EDITOR.CANCELLED': { target: 'closed', actions: ['clearDuplicateError', 'clearPending'] }
					}
				}
			}
		}
	}
});
